// Utilitaires pour HotelPageV2

export const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getStatusColor = (status) => {
  switch (status) {
    case "Good":
      return "bg-green-100 text-green-800";
    case "Unstable":
      return "bg-amber-100 text-amber-800";
    case "Warning":
      return "bg-yellow-100 text-yellow-800";
    case "Batterie":
      return "bg-red-100 text-red-700";
    case "Lost":
      return "bg-red-100 text-red-900";
    case "N/A":
      return "bg-red-100 text-red-500";
    case "Autre":
      return "bg-red-100 text-red-800";
    // Nouveaux statuts: tous fond rouge, texte légèrement différencié
    case "À équiper":
      return "bg-gray-100 text-gray-800";
    default:
      // Compatibilité si des anciens statuts historiques persistent côté UI
      if (status === "Failed" || status === "NOK") return "bg-red-100 text-red-800";
      if (status === "TBC") return "bg-blue-100 text-blue-800";
      return "bg-gray-100 text-gray-800";
  }
};

export const getBatteryColor = (voltage) => {
  if (!voltage) return "text-gray-400";
  const voltageNum = parseFloat(voltage);

  // Batterie en mV, seuil critique à 3.4V = 3400mV
  if (voltageNum < 3400) return "text-red-600 font-semibold";
  if (voltageNum < 3600) return "text-yellow-600";
  return "text-green-600";
};

export const formatVoltage = (voltage) => {
  if (!voltage) return "N/A";
  // Voltage en mV, conversion en V
  return `${(parseFloat(voltage) / 1000).toFixed(2)}V`;
};

export const getLastShowerVolumeColor = (lastShowerDate) => {
  if (!lastShowerDate) return "text-gray-400";

  const showerDate = new Date(lastShowerDate);
  if (Number.isNaN(showerDate.getTime())) return "text-gray-400";

  const now = new Date();
  const oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);

  if (showerDate < oneMonthAgo) return "text-red-600 font-semibold";
  return "text-gray-900 font-medium";
};

export const getConnectivityColor = (tabConnect) => {
  // Support pour les deux formats : ancien (string) et nouveau (object)
  const status = typeof tabConnect === "object" ? tabConnect?.status : tabConnect;

  switch (status) {
    case "OK":
      return "bg-green-100 text-green-800";
    case "NOK":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Fonction spécialisée pour comparer les numéros de chambre (exportée pour réutilisation)
export const compareRoomNumbers = (roomA, roomB) => {
  // Fonction pour extraire les parties numériques et alphabétiques
  const extractParts = (str) => {
    const match = str.match(/(\d+)|([A-Za-z]+)/g) || [];
    return match.map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? part : num;
    });
  };

  const partsA = extractParts(roomA);
  const partsB = extractParts(roomB);

  // Comparaison des parties une par une
  for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
    if (typeof partsA[i] !== typeof partsB[i]) {
      // Si les types sont différents, les nombres viennent avant les lettres
      return typeof partsA[i] === "number" ? -1 : 1;
    }
    if (partsA[i] !== partsB[i]) {
      return partsA[i] < partsB[i] ? -1 : 1;
    }
  }
  // Si toutes les parties sont égales, la chaîne la plus courte vient en premier
  return partsA.length - partsB.length;
};

export const sortRoomsByNumber = (rooms) => {
  return [...rooms].sort((a, b) => compareRoomNumbers(a.room_nb, b.room_nb));
};

export const filterRoomsByStatus = (rooms, statusFilter) => {
  if (statusFilter === "all") return rooms;
  return rooms.filter((room) => room.status === statusFilter);
};

export const getStatusOptions = () => [
  { value: "all", label: "Toutes", count: 0 },
  { value: "À équiper", label: "À équiper", count: 0 },
  { value: "Good", label: "Good", count: 0 },
  { value: "Unstable", label: "Unstable", count: 0 },
  { value: "Warning", label: "Warning", count: 0 },
  { value: "Batterie", label: "Batterie", count: 0 },
  { value: "Lost", label: "Lost", count: 0 },
  { value: "N/A", label: "N/A", count: 0 },
  { value: "Autre", label: "Autre", count: 0 },
  { value: "logs", label: "Logs", count: 0 },
];

export const updateStatusCounts = (statusOptions, rooms) => {
  const counts = rooms.reduce((acc, room) => {
    acc[room.status] = (acc[room.status] || 0) + 1;
    return acc;
  }, {});

  return statusOptions.map((option) => ({
    ...option,
    count: option.value === "all" ? rooms.length : counts[option.value] || 0,
  }));
};

// ===== NOUVELLES FONCTIONS DE TRI =====

// Configuration des colonnes de tri
export const SORT_COLUMNS = {
  ROOM_NUMBER: "room_number",
  STATUS: "status",
  LAST_CHANGE: "last_change",
  MODE: "mode",
  TAB_CONNECT: "tab_connect",
  BATTERY: "battery",
  LAST_SHOWER: "last_shower",
  RESET: "reset",
};

// Ordre de priorité pour les statuts
const STATUS_PRIORITY = {
  Lost: 1,
  Warning: 2,
  Batterie: 3,
  "N/A": 4,
  Autre: 5,
  Unstable: 7,
  Good: 8,
  "À équiper": 9,
};

// Fonction générique de tri
export const sortRooms = (rooms, sortColumn, sortDirection) => {
  const sortedRooms = [...rooms].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case SORT_COLUMNS.ROOM_NUMBER:
        // Tri intelligent pour LuniShower :
        // 1) Prioriser les chambres qui ont un lien avec une chambre "général" (rRoomNumber)
          // 2) Trier ces chambres par le numéro général (rRoomNumber), sinon fallback sur room_nb
        {
          const aMapped = a.rRoomNumber != null && a.rRoomNumber !== "";
          const bMapped = b.rRoomNumber != null && b.rRoomNumber !== "";

          // Toujours afficher d'abord les chambres mappées vers "général"
          if (aMapped !== bMapped) return aMapped ? -1 : 1;

          if (aMapped && bMapped) {
            const cmp = compareRoomNumbers(a.rRoomNumber, b.rRoomNumber);
            return sortDirection === "asc" ? cmp : -cmp;
          }

          const cmp = compareRoomNumbers(a.room_nb, b.room_nb);
          return sortDirection === "asc" ? cmp : -cmp;
        }

      case SORT_COLUMNS.STATUS:
        aValue = STATUS_PRIORITY[a.status] || 999;
        bValue = STATUS_PRIORITY[b.status] || 999;
        break;

      case SORT_COLUMNS.LAST_CHANGE:
        aValue = a.hb_journalier_per_day_pct == null ? 0 : Number(a.hb_journalier_per_day_pct);
        bValue = b.hb_journalier_per_day_pct == null ? 0 : Number(b.hb_journalier_per_day_pct);
        if (Number.isNaN(aValue)) aValue = 0;
        if (Number.isNaN(bValue)) bValue = 0;
        break;

      case SORT_COLUMNS.MODE:
        {
          const aNum = a.mode == null || a.mode === "" ? null : Number(a.mode);
          const bNum = b.mode == null || b.mode === "" ? null : Number(b.mode);

          // Si mode est numérique, on trie numériquement.
          if (aNum != null && bNum != null && !Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            aValue = aNum;
            bValue = bNum;
          } else {
            // Sinon, fallback string (compat avec l'ancien mode enum éventuel).
            aValue = String(a.mode ?? "").toLowerCase();
            bValue = String(b.mode ?? "").toLowerCase();
          }
        }
        break;

      case SORT_COLUMNS.TAB_CONNECT:
        // OK avant NOK - support pour les deux formats
        const aStatus = typeof a.tab_connect === "object" ? a.tab_connect?.status : a.tab_connect;
        const bStatus = typeof b.tab_connect === "object" ? b.tab_connect?.status : b.tab_connect;
        aValue = aStatus === "OK" ? 0 : aStatus === "NOK" ? 1 : 2;
        bValue = bStatus === "OK" ? 0 : bStatus === "NOK" ? 1 : 2;
        break;

      case SORT_COLUMNS.BATTERY:
        // Tri par voltage (mV)
        aValue = a.battery?.voltage ? parseFloat(a.battery.voltage) : -1;
        bValue = b.battery?.voltage ? parseFloat(b.battery.voltage) : -1;
        break;

      case SORT_COLUMNS.LAST_SHOWER:
        // Tri par date de dernière douche
        aValue = a.last_shower?.last_update ? new Date(a.last_shower.last_update).getTime() : 0;
        bValue = b.last_shower?.last_update ? new Date(b.last_shower.last_update).getTime() : 0;
        break;

      case SORT_COLUMNS.RESET:
        // Tri par nombre de resets (par défaut les plus élevés en premier)
        aValue = typeof a.reset_count === "number" ? a.reset_count : 0;
        bValue = typeof b.reset_count === "number" ? b.reset_count : 0;
        break;

      default:
        return 0;
    }

    // Tri standard pour les valeurs numériques et textuelles
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return sortedRooms;
};


// Fonction pour obtenir la prochaine direction de tri
export const getNextSortDirection = (currentColumn, newColumn, currentDirection) => {
  if (currentColumn !== newColumn) {
    // Nouvelle colonne : certaines commencent en desc (ex: RESET pour voir le plus de resets en premier)
    if (newColumn === SORT_COLUMNS.RESET) {
      return "desc";
    }
    return "asc";
  }
  // Même colonne : inverser la direction
  return currentDirection === "asc" ? "desc" : "asc";
};

// Fonction pour obtenir l'icône de tri
export const getSortIcon = (column, currentColumn, currentDirection) => {
  if (column !== currentColumn) {
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  if (currentDirection === "asc") {
    return (
      <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    );
  }

  return (
    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
    </svg>
  );
};

// ===== NOUVELLES FONCTIONS POUR LE FILTRAGE PAR STATUT =====

/**
 * Liste complète de tous les statuts possibles
 */
const ALL_POSSIBLE_STATUSES = ["À équiper", "Good", "Unstable", "Warning", "Batterie", "Lost", "N/A", "Autre"];

/**
 * Obtient la liste des statuts disponibles avec leurs compteurs
 * Affiche TOUS les statuts possibles, même ceux avec 0 chambre
 */
export const getAvailableStatuses = (rooms) => {
  // Compter les chambres par statut
  const statusCounts = rooms.reduce((acc, room) => {
    const status = room.status || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Créer la liste complète des statuts avec leurs informations
  const statuses = ALL_POSSIBLE_STATUSES.map((status) => ({
    value: status,
    label: status,
    count: statusCounts[status] || 0, // 0 si le statut n'existe pas
  }));

  // Ajouter les statuts "inconnus" qui ne sont pas dans la liste prédéfinie
  Object.keys(statusCounts).forEach((status) => {
    if (!ALL_POSSIBLE_STATUSES.includes(status)) {
      statuses.push({
        value: status,
        label: status,
        count: statusCounts[status],
      });
    }
  });

  // Trier par priorité (statuts critiques en premier)
  return statuses.sort((a, b) => {
    const priorityA = STATUS_PRIORITY[a.value] || 999;
    const priorityB = STATUS_PRIORITY[b.value] || 999;
    return priorityA - priorityB;
  });
};

/**
 * Filtre les chambres selon les statuts sélectionnés
 */
export const filterRoomsBySelectedStatuses = (rooms, selectedStatuses) => {
  if (!selectedStatuses || selectedStatuses.length === 0) {
    return [];
  }

  return rooms.filter((room) => selectedStatuses.includes(room.status));
};

/**
 * Obtient tous les statuts disponibles (pour initialisation)
 * Retourne tous les statuts possibles, pas seulement ceux présents
 */
export const getAllStatusValues = (rooms) => {
  // Commencer avec tous les statuts possibles
  const allStatuses = new Set(ALL_POSSIBLE_STATUSES);

  // Ajouter les statuts réellement présents dans les données
  rooms.forEach((room) => {
    if (room.status) {
      allStatuses.add(room.status);
    }
  });

  return Array.from(allStatuses);
};
